import { useState, useEffect, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import { TalkRoomSchedule } from '../../types';
import Button from '../common/Button';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Select from '../common/Select';

interface ScheduleTabProps {
  roomId: string;
}

const eventTypeLabels: Record<string, string> = {
  meeting: 'ミーティング',
  request: '要請対応',
  construction: '施工',
  personal: '個人予定',
  holiday: '休暇',
  facility: '設備点検',
};

const eventTypeColors: Record<string, string> = {
  meeting: '#22c55e',
  request: '#eab308',
  construction: '#3b82f6',
  personal: '#a855f7',
  holiday: '#ef4444',
  facility: '#f97316',
};

const initialFormData = {
  title: '',
  description: '',
  startTime: '',
  endTime: '',
  allDay: false,
  eventType: 'meeting',
  location: '',
};

export default function ScheduleTab({ roomId }: ScheduleTabProps) {
  const [schedules, setSchedules] = useState<TalkRoomSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedSchedule, setSelectedSchedule] = useState<TalkRoomSchedule | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const monthStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      const data = await api.get<{ items: TalkRoomSchedule[] }>(`/api/talkrooms/${roomId}/schedules?month=${monthStr}`);
      setSchedules(data.items || []);
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    } finally {
      setLoading(false);
    }
  }, [roomId, currentDate]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  // カレンダー計算
  const calendarData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days: Array<{ date: Date | null; schedules: TalkRoomSchedule[] }> = [];

    // 前月の空白
    for (let i = 0; i < startOffset; i++) {
      days.push({ date: null, schedules: [] });
    }

    // 当月の日付
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const daySchedules = schedules.filter(s => {
        if (s.date === dateStr) return true;
        if (s.startTime) {
          const sDate = new Date(s.startTime).toLocaleDateString('sv-SE'); // YYYY-MM-DD
          return sDate === dateStr;
        }
        return false;
      });
      days.push({ date, schedules: daySchedules });
    }

    return days;
  }, [currentDate, schedules]);

  // 選択日の予定
  const selectedDateSchedules = useMemo(() => {
    if (!selectedDate) return [];
    const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
    return schedules.filter(s => {
      if (s.date === dateStr) return true;
      if (s.startTime) {
        const sDate = new Date(s.startTime).toLocaleDateString('sv-SE');
        return sDate === dateStr;
      }
      return false;
    }).sort((a, b) => {
      const timeA = a.startTime ? new Date(a.startTime).getTime() : 0;
      const timeB = b.startTime ? new Date(b.startTime).getTime() : 0;
      return timeA - timeB;
    });
  }, [selectedDate, schedules]);

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDate(today);
  };

  const isToday = (date: Date | null) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date | null) => {
    if (!date || !selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const handleDateClick = (date: Date | null) => {
    if (date) setSelectedDate(date);
  };

  const getScheduleColor = (schedule: TalkRoomSchedule) => {
    if (schedule.color) return schedule.color;
    if (schedule.type === 'request') return '#eab308';
    if (schedule.type === 'construction') return '#3b82f6';
    if (schedule.eventType) return eventTypeColors[schedule.eventType] || '#6b7280';
    return '#3b82f6';
  };

  const getScheduleLabel = (schedule: TalkRoomSchedule) => {
    if (schedule.type === 'request') return '要請';
    if (schedule.type === 'construction') return '施工';
    if (schedule.eventType) return eventTypeLabels[schedule.eventType] || schedule.eventType;
    return '';
  };

  const formatTime = (isoStr: string) => {
    const d = new Date(isoStr);
    const h = d.getHours();
    const m = String(d.getMinutes()).padStart(2, '0');
    const period = h < 12 ? '午前' : '午後';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${period} ${String(h12).padStart(2, '0')}:${m}`;
  };

  const weekDayStr = (date: Date) => {
    return ['日', '月', '火', '水', '木', '金', '土'][date.getDay()];
  };

  // 新しい予定作成
  const handleOpenCreate = () => {
    const dateStr = selectedDate
      ? selectedDate.toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10);
    setFormData({
      ...initialFormData,
      startTime: `${dateStr}T09:00`,
      endTime: `${dateStr}T10:00`,
    });
    setShowCreateModal(true);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || submitting) return;
    setSubmitting(true);
    try {
      await api.post('/api/calendar', formData);
      setShowCreateModal(false);
      setFormData(initialFormData);
      fetchSchedules();
    } catch (error) {
      console.error('Failed to create event:', error);
      toast.error('予定の作成に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleRefresh = () => {
    fetchSchedules();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* ヘッダー：ナビゲーション */}
      <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <button
              onClick={goToToday}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              今日
            </button>
            <button onClick={prevMonth} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
              <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button onClick={nextMonth} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
              <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <span className="text-sm font-bold text-gray-900 dark:text-white ml-1">
              {currentDate.getFullYear()}.{String(currentDate.getMonth() + 1).padStart(2, '0')}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleRefresh}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              title="更新"
            >
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              onClick={handleOpenCreate}
              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md flex items-center gap-1"
            >
              新しい予定
            </button>
          </div>
        </div>
      </div>

      {/* カレンダーグリッド */}
      <div className="flex-shrink-0 px-2 pt-1">
        <div className="grid grid-cols-7">
          {/* 曜日ヘッダー */}
          {['日', '月', '火', '水', '木', '金', '土'].map((day, i) => (
            <div
              key={day}
              className={`text-center py-1 text-[10px] font-medium ${
                i === 0 ? 'text-red-500' : i === 6 ? 'text-blue-500' : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {day}
            </div>
          ))}

          {/* 日付セル */}
          {calendarData.map((day, idx) => {
            const hasSchedules = day.schedules.length > 0;
            return (
              <div
                key={idx}
                onClick={() => handleDateClick(day.date)}
                className={`min-h-[52px] p-0.5 cursor-pointer border-t border-gray-100 dark:border-gray-800 transition-colors ${
                  isSelected(day.date)
                    ? 'bg-blue-50 dark:bg-blue-900/20'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                } ${!day.date ? 'bg-gray-50/50 dark:bg-gray-900/50' : ''}`}
              >
                {day.date && (
                  <>
                    <div className="flex justify-center">
                      <span
                        className={`text-[11px] leading-none ${
                          isToday(day.date)
                            ? 'w-5 h-5 flex items-center justify-center bg-blue-500 text-white rounded-full font-bold'
                            : isSelected(day.date) && !isToday(day.date)
                            ? 'w-5 h-5 flex items-center justify-center bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200 rounded-full'
                            : day.date.getDay() === 0
                            ? 'text-red-500'
                            : day.date.getDay() === 6
                            ? 'text-blue-500'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {day.date.getDate()}
                      </span>
                    </div>
                    <div className="mt-0.5 space-y-px">
                      {day.schedules.slice(0, 2).map((schedule) => (
                        <div
                          key={schedule.id}
                          className="text-[8px] leading-tight px-0.5 rounded truncate text-white"
                          style={{ backgroundColor: getScheduleColor(schedule) }}
                          title={schedule.title}
                        >
                          {schedule.title.length > 8 ? schedule.title.slice(0, 8) + '…' : schedule.title}
                        </div>
                      ))}
                      {day.schedules.length > 2 && (
                        <div className="text-[8px] text-gray-400 text-center">
                          +{day.schedules.length - 2}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 選択日の予定リスト */}
      <div className="flex-1 overflow-auto border-t border-gray-200 dark:border-gray-700">
        {selectedDate && (
          <>
            {/* 選択日ヘッダー */}
            <div className="sticky top-0 z-10 px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <span className="text-sm font-bold text-gray-900 dark:text-white">
                {selectedDate.getMonth() + 1}.{selectedDate.getDate()} {weekDayStr(selectedDate)}
              </span>
            </div>

            {selectedDateSchedules.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-xs">予定はありません</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {selectedDateSchedules.map((schedule) => (
                  <button
                    key={schedule.id}
                    onClick={() => setSelectedSchedule(schedule)}
                    className="w-full text-left px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex gap-2">
                      {/* 時間 */}
                      <div className="flex-shrink-0 w-[72px] text-[11px] text-gray-500 dark:text-gray-400 pt-0.5">
                        {schedule.startTime && !schedule.allDay ? (
                          <div>
                            <div>{formatTime(schedule.startTime)}</div>
                            {schedule.endTime && (
                              <div>{formatTime(schedule.endTime)}</div>
                            )}
                          </div>
                        ) : schedule.allDay ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">終日</span>
                        ) : schedule.date ? (
                          <span className="text-[10px]">{schedule.date}</span>
                        ) : null}
                      </div>

                      {/* カラーバー */}
                      <div
                        className="flex-shrink-0 w-1 rounded-full self-stretch"
                        style={{ backgroundColor: getScheduleColor(schedule) }}
                      />

                      {/* 内容 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span
                            className="text-[9px] px-1 py-px rounded text-white font-medium flex-shrink-0"
                            style={{ backgroundColor: getScheduleColor(schedule) }}
                          >
                            {getScheduleLabel(schedule)}
                          </span>
                          <span className="text-xs font-medium text-gray-900 dark:text-white truncate">
                            {schedule.title}
                          </span>
                        </div>
                        {schedule.location && (
                          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 truncate flex items-center gap-0.5">
                            <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {schedule.location}
                          </div>
                        )}
                        {schedule.organizerName && (
                          <div className="text-[10px] text-gray-400 mt-0.5">
                            担当: {schedule.organizerName}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* 凡例 */}
      <div className="flex-shrink-0 px-3 py-1.5 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-blue-500"></span>
            <span className="text-[10px] text-gray-500">施工</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-yellow-500"></span>
            <span className="text-[10px] text-gray-500">要請</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-green-500"></span>
            <span className="text-[10px] text-gray-500">会議</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-purple-500"></span>
            <span className="text-[10px] text-gray-500">個人</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-red-500"></span>
            <span className="text-[10px] text-gray-500">休暇</span>
          </div>
        </div>
      </div>

      {/* 予定詳細モーダル */}
      <Modal
        isOpen={!!selectedSchedule}
        onClose={() => setSelectedSchedule(null)}
        title="予定詳細"
      >
        {selectedSchedule && (
          <div className="space-y-4">
            {/* タグ */}
            <div className="flex items-center gap-2">
              <span
                className="px-2 py-1 text-xs rounded text-white font-medium"
                style={{ backgroundColor: getScheduleColor(selectedSchedule) }}
              >
                {getScheduleLabel(selectedSchedule)}
              </span>
            </div>

            {/* タイトル */}
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
              {selectedSchedule.title}
            </h4>

            {/* 日時 */}
            <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
              <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <div>
                {selectedSchedule.allDay ? (
                  <span>
                    {selectedSchedule.date && new Date(selectedSchedule.date + 'T00:00:00').toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })}
                    <span className="ml-1 text-xs text-gray-400">(終日)</span>
                  </span>
                ) : selectedSchedule.startTime ? (
                  <div>
                    <div>{new Date(selectedSchedule.startTime).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' })}</div>
                    <div>
                      {formatTime(selectedSchedule.startTime)}
                      {selectedSchedule.endTime && ` 〜 ${formatTime(selectedSchedule.endTime)}`}
                    </div>
                  </div>
                ) : selectedSchedule.date ? (
                  <span>{new Date(selectedSchedule.date + 'T00:00:00').toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                ) : (
                  <span>日時未定</span>
                )}
              </div>
            </div>

            {/* 場所 */}
            {selectedSchedule.location && (
              <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{selectedSchedule.location}</span>
              </div>
            )}

            {/* 説明 */}
            {selectedSchedule.description && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {selectedSchedule.description}
              </div>
            )}

            {/* 担当者 */}
            {selectedSchedule.organizerName && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>担当: {selectedSchedule.organizerName}</span>
              </div>
            )}

            {/* 参加者 */}
            {selectedSchedule.attendees && selectedSchedule.attendees.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-2">参加者 ({selectedSchedule.attendees.length}名)</p>
                <div className="flex flex-wrap gap-1">
                  {selectedSchedule.attendees.map((a: any) => (
                    <span key={a.id} className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 rounded text-xs">
                      {a.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end pt-4 border-t dark:border-gray-700">
              <Button variant="secondary" onClick={() => setSelectedSchedule(null)}>
                閉じる
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* 予定作成モーダル */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); setFormData(initialFormData); }}
        title="新しい予定"
        size="lg"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <Input
            label="タイトル"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            placeholder="予定のタイトルを入力"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">説明</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              rows={2}
              placeholder="説明を入力（任意）"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="allDay"
              name="allDay"
              checked={formData.allDay}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="allDay" className="text-sm text-gray-700 dark:text-gray-300">終日</label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="開始"
              name="startTime"
              type={formData.allDay ? 'date' : 'datetime-local'}
              value={formData.allDay ? formData.startTime.slice(0, 10) : formData.startTime}
              onChange={handleChange}
              required
            />
            <Input
              label="終了"
              name="endTime"
              type={formData.allDay ? 'date' : 'datetime-local'}
              value={formData.allDay ? formData.endTime.slice(0, 10) : formData.endTime}
              onChange={handleChange}
              required
            />
          </div>
          <Select label="種別" name="eventType" value={formData.eventType} onChange={handleChange}>
            {Object.entries(eventTypeLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Select>
          <Input
            label="場所"
            name="location"
            value={formData.location}
            onChange={handleChange}
            placeholder="場所を入力（任意）"
          />
          <div className="flex justify-end gap-2 pt-4 border-t dark:border-gray-700">
            <Button
              type="button"
              variant="secondary"
              onClick={() => { setShowCreateModal(false); setFormData(initialFormData); }}
            >
              キャンセル
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? '作成中...' : '登録'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
